import { Gift } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { useAuth } from '../hooks/useAuth';

export default function GiftCardPurchaseSuccess() {
  const { t, i18n } = useTranslation('giftCard');
  const { isAuthenticated } = useAuth();
  const [params] = useSearchParams();

  // Parse the optional scheduled delivery date from the success URL. Backend
  // appends &deliverAt=YYYY-MM-DD when the buyer picked a future date.
  const deliverAt = params.get('deliverAt');
  const scheduled = !!deliverAt && /^\d{4}-\d{2}-\d{2}$/.test(deliverAt);
  let scheduledDate = '';
  if (scheduled && deliverAt) {
    const [y, m, d] = deliverAt.split('-').map(Number);
    // Construct a local-noon date so locale formatting never lands on the
    // previous day in any timezone.
    scheduledDate = new Date(y, m - 1, d, 12).toLocaleDateString(i18n.language, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  // Logged-in users go to their My Gift Cards tab; guests are pointed at
  // sign-in so they can claim a self-purchased balance from the email link
  // (the redeem flow requires an account).
  const cta = isAuthenticated
    ? { to: '/dashboard?tab=giftCards', label: t('success.viewMyCards') }
    : { to: '/login', label: t('success.signInToRedeem') };
  return (
    <div className="max-w-md mx-auto px-4 py-16 text-center">
      <Helmet>
        <title>{t('meta.titleSuccess')}</title>
        <meta name="description" content={t('meta.descriptionSuccess')} />
        {/* Post-purchase confirmation page — keep out of search indexes. */}
        <meta name="robots" content="noindex" />
      </Helmet>
      <span
        className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/10 mb-4"
        aria-hidden="true"
      >
        <Gift className="w-7 h-7 text-primary" strokeWidth={2} />
      </span>
      <h1 className="text-2xl font-bold text-gray-900 mb-2">
        {scheduled ? t('success.titleScheduled') : t('success.title')}
      </h1>
      <p className="text-gray-600 mb-6">
        {scheduled ? t('success.bodyScheduled', { date: scheduledDate }) : t('success.body')}
      </p>
      <Link to={cta.to} className="text-primary underline">
        {cta.label}
      </Link>
    </div>
  );
}
